import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import "../assets/style/DeliveryTypes.css"
import { useTranslate, useJSONState, useStaticData } from '../hooks';
import { TextField, Grid, Container, Group, Centralizer, Select, ChangeDialog, ConfirmDialog, Button, Accordion, DataTable, Form, Switch } from '../components';
import { deepCopyOf, areIdentical, sleep, dialog, Dialog } from '../utils';
import { CarriersuitesProductsType, CarrierType, DeliveryTypeType, TradeAreaType } from '../types';
import { FaPenToSquare} from "react-icons/fa6";

/*
NSN 
1. Går just ny inte Ändra deliveryType då den uppdaterar sig själv och tappar fokus direkt.
2. Save ska göra API-anrop
3. Delete ska göra API-anrop, eller?
*/

export default function DeliveryTypes(){
  const api = useAxiosPrivate();
  const [changed, setChanged] = useState(false)
  const { staticData } = useStaticData()
  const { t } = useTranslate();
  const PAGE_SIZE = 25;
  const [ deliveryTypes, setDeliveryTypes] = useState<DeliveryTypeType[]>()
  const [ carriers, setCarriers ] = useState<CarrierType[]>()
  const location = useLocation()
  const [ deliveryType, setProp, setDeliveryType ] = useJSONState<DeliveryTypeType>()
  const [ originalDeliveryType, setOriginalDeliveryType ] = useState<DeliveryTypeType>()
  const [ dialogData, setDialogData ] = useState<({carrierSuiteId: string, tradeArea: TradeAreaType, serviceCodes:Set<string>}) | undefined>()
  const [ triggerRerender, setTriggerRerender ] = useState(false)

  // useEffect(() => {
  //   let dtInPath = location.pathname.replaceAll("/DeliveryTypes/", "")
  //   if(dtInPath === "") return
  //   const dt = deliveryTypes?.find(dt => dt.deliveryType === dtInPath)
  //   console.log({dtInPath, dt})

  //   if(dt){
  //     setDeliveryType(dt)
  //     setOriginalDeliveryType(dt)
  //   }

  // }, [location, deliveryTypes])

  useEffect(() => {
    if(!areIdentical(deliveryType, originalDeliveryType)) !changed && setChanged(true)
    else changed && setChanged(false)
  }, [deliveryType, originalDeliveryType])

  useEffect(() => {
    getCarriers();
  }, [])

  const updateDB = async (type: "save" | "delete") => {
    try{
      if(originalDeliveryType){
        if(type === "save"){
          await api.put({endpoint:`/deliveryTypes/${originalDeliveryType.id}`, 
            data: deliveryType,
            mount: {
              loadingText: t("save"),
              onLoadText: t("saved")
            }
          })
        }else{
          await api.delete({endpoint:`/deliveryTypes/${originalDeliveryType.id}`, 
            mount: {
              loadingText: t("deleting"),
              onLoadText: t("deleted")
            }
          })
        }
      }else{
        if(type === "delete") throw new Error("Missing deliveryType to delete..")
  
        await api.post({endpoint: "/deliveryTypes", 
          data: deliveryType,
          mount: {
            loadingText: t("save"),
            onLoadText: t("saved")
          }
        })
        //window.history.replaceState(null, "", `/DeliveryTypes/${encodeURIComponent(deliveryType.deliveryType)}`);
      }
      
      if(type === "save"){
        setOriginalDeliveryType(deliveryType)
      }
      else{
        setDeliveryType({} as DeliveryTypeType)
        setOriginalDeliveryType({} as DeliveryTypeType)
      }
      setTimeout(() => setTriggerRerender((v) => !v), 50)      
    }
    catch(e){
      console.error(e)
    }
  }

  const fetchDeliveryTypes = async ({page, snapshot, pageSize}: {
    page:number,
    snapshot: string,
    pageSize: number,
  }) => {      
    try{
      const preDate = (new Date()).getTime()
      const response = await api.get({
        endpoint: "/deliveryTypes",
        params: { page, snapshot, pageSize },
        mount: null
      })

      // For the search we want an indication of search. But not for page switch
      if(snapshot === ""){
        const afterDate = (new Date()).getTime()
        if(afterDate - preDate < 400) await sleep(400 - (afterDate - preDate))
      }          

      let data = response?.data?.data

      if(!data) return Promise.resolve()

      setDeliveryTypes(deepCopyOf(data));

      const pages = response.data?.pages
      snapshot = response.data?.snapshot   
      
      data = data.reduce((prev:Partial<DeliveryTypeType>[], cur:DeliveryTypeType) => {
        const d = cur as Partial<DeliveryTypeType>
        delete d.dom
        delete d.eu
        delete d.ex
        prev.push(d)
        return prev;
      }, [])

      return Promise.resolve({data, snapshot, pages})
    }
    catch(e){
      if(e?.response?.status !== 401) console.error(e)
      return Promise.resolve({data:[], snapshot, pages: 0})
    }
  }

  const getCarriers = async () => {
    const response = await api.get({endpoint: "/carriers", mount:null})
    setCarriers(response?.data ?? [])  
  }

  const getServices = () => {
    if(!dialogData) return []
    return staticData?.carriersuitesProducts[dialogData.carrierSuiteId][dialogData.tradeArea]
      ?.find(p => p.value === deliveryType[dialogData.tradeArea]?.value)
      ?.serviceCodes ?? []
  }

  const renderServices = (tradeArea: TradeAreaType, carrierSuiteId: string) => {
    let csuiteServices = deepCopyOf(staticData?.carriersuitesProducts[carrierSuiteId][tradeArea])
      ?.find(p => p.value === deliveryType[tradeArea]?.value)
      ?.serviceCodes
    
    if(!csuiteServices || !csuiteServices?.length) return <></>

    //let usedSCs = new Set<string>()
    return <Group label={t("additionalServices")}>
      <>
      {
        deliveryType[tradeArea]?.serviceCodes?.map((sc, i) => {
          return <p>- {csuiteServices?.find(css => css.value === sc)?.name}</p>
        })
      }
      <FaPenToSquare 
        onClick={() => setDialogData({tradeArea, carrierSuiteId, serviceCodes: new Set(deliveryType[tradeArea]?.serviceCodes)})}
      />
    </>
    </Group>
  }

  const renderProducts = () => {
    if(!deliveryType?.carrierId || !carriers){
      return <>
      </>
    }

    const carrierSuiteId = carriers.find(c => c.carrierId === deliveryType.carrierId)?.carrierSuiteId
    if(!carrierSuiteId) return <></>

    const tradeAreas: {tradeArea: TradeAreaType, name:string}[] = [
      {tradeArea: "dom", name: "domestic"},
      {tradeArea: "eu", name: "europe"},
      {tradeArea: "ex", name: "export"},
    ]    

    return <div style={{display:"flex", gap:"1rem", width:"100%"}}>
      {
        tradeAreas.map(ta => {
          console.log({ta, carrierSuiteId, staticData})
          if(!staticData?.carriersuitesProducts[carrierSuiteId][ta.tradeArea]?.length) return <></>

          const flagClass = ta.tradeArea === "dom" ? "mr-2 flag scale-150 " + staticData.warehouse.countryCode.toLowerCase() :
            ta.tradeArea === "eu" ? "mr-2 flag scale-150 eu": "mr-2 flag scale-150 ex"

          const data = staticData?.carriersuitesProducts[carrierSuiteId][ta.tradeArea] ?? []
          
          return <div 
            //label={t(ta.name)}
            style={{border:"2px solid hsla(0, 0%, 80%, .3)", borderRadius:"10px", padding:".6rem", width:"32%"}}
          >
            <div className='flex gap-1'>
              <div className={flagClass}/>
              <h2>{t(ta.name)}</h2>
            </div>            
            <br/>
            <Select 
              data={deliveryType.returnDocuments ?
                data.filter(csp => returnAvailable(ta.tradeArea, csp.value)):
                data
              }
              label={t("service")} 
              value={deliveryType[ta.tradeArea]?.value}
              onChange={(e) => setProp(e.currentTarget.value, ta.tradeArea, "value")}
            />
            { !deliveryType.returnDocuments || returnAvailable(ta.tradeArea, deliveryType?.[ta.tradeArea]?.value) ?
              renderServices(ta.tradeArea, carrierSuiteId): <></>
            }
          </div>
        })
      }
    </div>    
  }

  const returnAvailable = (tradeArea?: TradeAreaType, productCode?: string) => {    
    //if(!deliveryType.carrierSuiteId) return false
    const csp = staticData?.carriersuitesProducts[deliveryType?.carrierSuiteId]
    if(!csp) return false;

    if(tradeArea && productCode) return csp[tradeArea]?.some(csp => csp.value == productCode && csp.returnAvailable)

    return csp["dom"]?.some(csp => !!csp.returnAvailable)
    || csp["eu"]?.some(csp => !!csp.returnAvailable)
    || csp["ex"]?.some(csp => !!csp.returnAvailable)
  }

  return <div className='schipt-deliverytypes'>
    {dialogData &&
      <Dialog>
        <Container>
          {
            getServices().map(sc => {
              return <Switch 
                label={sc.name} 
                checked={dialogData.serviceCodes.has(sc.value)}
                disabled={!dialogData.serviceCodes.has(sc.value) && sc.noCombined?.some(nc => dialogData.serviceCodes.has(nc))}
                onChange={(e) => {
                  const { checked } = e.currentTarget;
                  
                  const newObj = { ...dialogData }; // shallow copy
                  if (!checked) newObj.serviceCodes.delete(sc.value);
                  else newObj.serviceCodes.add(sc.value);

                  setDialogData(newObj);
                }}
              />
            })
          }
        </Container>
        <Button _type="button" variant='add' onClick={() => {
          const dt = deepCopyOf(deliveryType)
          if(dt[dialogData.tradeArea] !== undefined) {
            dt[dialogData.tradeArea]!.serviceCodes = Array.from(dialogData.serviceCodes);
            setDeliveryType(dt);
          }            
          setDialogData(undefined)
        }}>{t("ok")}</Button>
      </Dialog>
    }
    <Form onSubmit={() => updateDB("save")}>
      <Centralizer>
        <Container style={{
          height: "40vh",
          maxHeight: "50vh",
          position: "static",
          width: "clamp(1000px, 80%, 80vw)"
        }}>
          {deliveryType?.id == undefined ? <></> :
            <Group>
              <Container>
                <Grid>
                  <Select 
                    data={carriers ?? []}
                    valueKey="carrierId"
                    required={true}
                    value={deliveryType.carrierId}
                    label={t("carrierId")}
                    onChange={(e) => {
                      const { value} = e.currentTarget
                      let dt = deepCopyOf(deliveryType)
                      dt.carrierId = value
                      dt.carrierSuiteId = carriers?.find(c => c.carrierId === value)?.carrierSuiteId ?? ""
                      setDeliveryType(dt)
                    }}
                  />  
                  {!returnAvailable() ? <></> :
                    <Switch 
                      label={t("includeReturnDocuments")}
                      checked={deliveryType.returnDocuments}
                      onChange={(e) => setProp(e.currentTarget.checked, "returnDocuments")}
                    />
                  }                 
                </Grid>
                { 
                  renderProducts()
                } 
                <Grid>
                  <TextField
                    value={deliveryType.name}
                    title={t("fieldInfo.deliveryTypeName")}
                    onChange={(e) => setProp(e.currentTarget.value, "name")}
                    required={true}
                  >{t("name")}</TextField>
                  <TextField
                    value={deliveryType.id}
                    title={t("fieldInfo.deliveryTypeId")}
                    onChange={(e) => setProp(e.currentTarget.value, "id")}
                  >{t("id")}</TextField>
                </Grid>
              </Container>  
              { originalDeliveryType?.id == undefined ? <></> : 
                <Button _type='button' variant='delete'
                  onClick={async () => {
                    const r = await dialog.open({
                      sure: {name: "", type:"switch", required:true}
                    }, {title:t("sureDeleteDeliveryType")})
                    if(!r) return
                    updateDB("delete");
                  }}
                >{t("delete")}</Button> 
              }                         
            </Group>                     
          }        
          <Accordion label={t("deliveryTypes")} defaultOpen={true}>
            <Container>
              <DataTable 
                fetchData={fetchDeliveryTypes}
                rerender={triggerRerender}
                pageSize={PAGE_SIZE}
                hideColumns={["carrierId"]}
                style={{maxHeight:"30rem"}} 
                openCallback={async (_, dt:DeliveryTypeType) => {
                  //window.history.replaceState(null, "", `/DeliveryTypes/${encodeURIComponent(dt.deliveryType)}`);
                  const realDt = deliveryTypes?.find(d => d.id === dt.id)
                  if(realDt){
                    setDeliveryType(realDt);
                    setOriginalDeliveryType(realDt)
                  }
                }}
              />
            </Container>
            <Button _type='button' variant='add' style={{ display: changed ? 'none' : 'inline-block' }}
              onClick={() => {
                setOriginalDeliveryType(undefined as unknown as DeliveryTypeType)
                setDeliveryType({
                  id: "",
                  name: "",
                  carrierId: "",
                  carrierSuiteId: "",
                  bookingCancelable: false,
                  bookingUpdateable: false,
                  returnDocuments: false
                })
              }}
            >{t("createNew")}</Button>
          </Accordion>
        </Container>
      </Centralizer>
      <ChangeDialog show={changed} 
        undo={async () => {
          // If "Changed" carrier is a "new" carrier, then remove it.
          if(originalDeliveryType) setDeliveryType(originalDeliveryType)
          else setDeliveryType(undefined as unknown as DeliveryTypeType)
        }}      
      /> 
    </Form>    
  </div>
}